<template>
  <div class="form-container">
    <h1 class="mb-4">建物情報編集</h1>
    <form @submit.prevent="handleSubmit" @keydown.enter.prevent>
      <div class="mb-3">
        <label for="customer" class="form-label">顧客 <span class="badge text-bg-danger">必須</span></label>
        <select id="customer" class="form-select" v-model="building.m_customer_id" required>
          <option value="" disabled selected>選択してください</option> <!-- placeholder -->
          <option v-for="customer in customers" :key="customer.id" :value="customer.id">{{ customer.customer_nm }}</option>
        </select>
        <div v-if="errorMessages.m_customer_id" class="alert alert-danger mt-2">{{ errorMessages.m_customer_id[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="buildingType" class="form-label">区分 <span class="badge text-bg-danger">必須</span></label>
        <select id="buildingType" class="form-select" v-model="building.m_building_type_id" required>
          <option value="" disabled selected>選択してください</option> <!-- placeholder -->
          <option v-for="buildingType in buildingTypes" :key="buildingType.id" :value="buildingType.id">{{ buildingType.building_type_nm }}</option>
        </select>
        <div v-if="errorMessages.m_building_type_id" class="alert alert-danger mt-2">{{ errorMessages.m_building_type_id[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="buildingName" class="form-label">建物名 <span class="badge text-bg-danger">必須</span></label>
        <input v-model="building.building_nm" type="text" placeholder="建物名" class="form-control" id="buildingName" required>
        <div v-if="errorMessages.building_nm" class="alert alert-danger mt-2">{{ errorMessages.building_nm[0] }}</div>
      </div>

      <PostalCodeInput
        v-model="building.postal_code"
        @address="({ region, locality, street }) => {
          building.prefecture = region;
          building.city = locality;
          building.other_address = street;
        }"
      />

      <div class="mb-3">
        <label for="prefecture" class="form-label">都道府県</label>
        <input v-model="building.prefecture" type="text" placeholder="都道府県" class="form-control" id="prefecture">
        <div v-if="errorMessages.prefecture" class="alert alert-danger mt-2">{{ errorMessages.prefecture[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="city" class="form-label">市区町村</label>
        <input v-model="building.city" type="text" placeholder="市区町村" class="form-control" id="city">
        <div v-if="errorMessages.city" class="alert alert-danger mt-2">{{ errorMessages.city[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="otherAddress" class="form-label">その他の住所</label>
        <input v-model="building.other_address" type="text" placeholder="その他の住所" class="form-control" id="otherAddress">
        <div v-if="errorMessages.other_address" class="alert alert-danger mt-2">{{ errorMessages.other_address[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="telNum" class="form-label">電話番号</label>
        <input v-model="building.tel_num" type="text" placeholder="電話番号" class="form-control" id="telNum">
        <div v-if="errorMessages.tel_num" class="alert alert-danger mt-2">{{ errorMessages.tel_num[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="contractDt" class="form-label">契約日</label>
        <input v-model="building.contract_dt" type="date" placeholder="契約日" class="form-control" id="contractDt">
        <div v-if="errorMessages.contract_dt" class="alert alert-danger mt-2">{{ errorMessages.contract_dt[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="inChargeNm" class="form-label">担当者</label>
        <input v-model="building.in_charge_nm" type="text" placeholder="担当者" class="form-control" id="inChargeNm">
        <div v-if="errorMessages.in_charge_nm" class="alert alert-danger mt-2">{{ errorMessages.in_charge_nm[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="inChargePhoneNum" class="form-label">携帯番号</label>
        <input v-model="building.in_charge_phone_num" type="text" placeholder="携帯番号" class="form-control" id="inChargePhoneNum">
        <div v-if="errorMessages.in_charge_phone_num" class="alert alert-danger mt-2">{{ errorMessages.in_charge_phone_num[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="inChargeEmail" class="form-label">担当者メールアドレス</label>
        <input v-model="building.in_charge_email" type="text" placeholder="担当者メールアドレス" class="form-control" id="inChargeEmail">
        <div v-if="errorMessages.in_charge_email" class="alert alert-danger mt-2">{{ errorMessages.in_charge_email[0] }}</div>
      </div>

      <div class="btn-area">
        <a class="btn btn-secondary mx-1" @click="$router.back()">戻る</a>
        <button type="submit" class="btn btn-success mx-1" :disabled="isSubmitting">更新</button>
      </div>
    </form>

    <div v-if="showCustomerChangeModal" class="customer-change-modal" role="dialog" aria-modal="true" aria-labelledby="customerChangeTitle">
      <div class="customer-change-modal__backdrop" @click="!isSubmitting && closeCustomerChangeModal()"></div>
      <div class="customer-change-modal__dialog">
        <h2 id="customerChangeTitle" class="h5 mb-3">顧客変更の適用開始年月</h2>
        <p class="mb-2">
          「{{ originalCustomerName }}」から「{{ selectedCustomerName }}」へ変更します。
        </p>
        <p class="small text-muted mb-3">
          変更後顧客の請求締日（{{ selectedCustomerDeadlineLabel }}締め）を基準に初期値を設定しています。
          指定年月より前に作成済みの予定表・請求書は変更されません。
        </p>
        <div class="mb-3">
          <label for="customerUpdateFrom" class="form-label">年間作業予定の適用開始年月</label>
          <input
            id="customerUpdateFrom"
            v-model="customerUpdateFrom"
            type="month"
            class="form-control"
            required
          >
          <div v-if="customerUpdateFromError" class="text-danger small mt-1">{{ customerUpdateFromError }}</div>
        </div>
        <div class="d-flex justify-content-end gap-2">
          <button type="button" class="btn btn-secondary" :disabled="isSubmitting" @click="closeCustomerChangeModal">キャンセル</button>
          <button type="button" class="btn btn-success" :disabled="isSubmitting" @click="submitWithCustomerChange">
            この年月以降を変更して更新
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, onMounted, watchEffect } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { getCustomers, getBuildingTypes, getBuilding, updateBuilding } from '@/api/axios';
import PostalCodeInput from '@/components/shared/PostalCodeInput.vue';
import {
  billingDeadlineLabel,
  defaultCustomerChangeEffectiveMonth
} from '@/utils/customerChangeEffectiveMonth';

export default {
  components: {
    PostalCodeInput
  },
  setup() {
    const title = ref('建物情報編集');

    watchEffect(() => {
      document.title = title.value;
    });

    const customers = ref([]);
    const buildingTypes = ref([]);
    const building = ref({
      m_customer_id: '',
      m_building_type_id: '',
      building_nm: '',
      postal_code: '',
      prefecture: '',
      city: '',
      other_address: '',
      tel_num: '',
      contract_dt: '',
      in_charge_nm: '',
      in_charge_phone_num: '',
      in_charge_email: ''
      // 他のフィールド
    });
    const originalCustomerId = ref(null);
    const showCustomerChangeModal = ref(false);
    const customerUpdateFrom = ref('');
    const customerUpdateFromError = ref('');
    const isSubmitting = ref(false);

    // エラーメッセージ用のリアクティブプロパティ
    const errorMessages = ref({});

    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const buildingId = route.params.id; // URLから建物情報IDを取得

    const loadBuildingData = async () => {
      try {
        const response = await getBuilding(buildingId, { include_visibility_hidden: true });
        building.value = response.data;
        originalCustomerId.value = response.data.m_customer_id;
      } catch (error) {
        console.error('建物情報データの取得に失敗しました:', error);
      }

      try {
        const response = await getCustomers({ include_visibility_hidden: true });
        customers.value = response.data.customers;
      } catch (error) {
        console.error('顧客情報ーデータの取得に失敗しました:', error);
      }

      try {
        const response = await getBuildingTypes({ include_visibility_hidden: true });
        buildingTypes.value = response.data.building_types;
      } catch (error) {
        console.error('建物種別情報ーデータの取得に失敗しました:', error);
      }
    };

    const selectedCustomer = computed(() =>
      customers.value.find(customer => String(customer.id) === String(building.value.m_customer_id))
    );
    const originalCustomer = computed(() =>
      customers.value.find(customer => String(customer.id) === String(originalCustomerId.value))
    );
    const selectedCustomerName = computed(() => selectedCustomer.value?.customer_nm || '変更後の顧客');
    const originalCustomerName = computed(() => originalCustomer.value?.customer_nm || '変更前の顧客');
    const selectedCustomerDeadlineLabel = computed(() =>
      billingDeadlineLabel(selectedCustomer.value?.billing_deadline)
    );

    const customerChanged = () =>
      String(building.value.m_customer_id) !== String(originalCustomerId.value);

    const openCustomerChangeModal = () => {
      customerUpdateFrom.value = defaultCustomerChangeEffectiveMonth(
        selectedCustomer.value?.billing_deadline
      );
      customerUpdateFromError.value = '';
      showCustomerChangeModal.value = true;
    };

    const closeCustomerChangeModal = () => {
      showCustomerChangeModal.value = false;
      customerUpdateFromError.value = '';
    };

    const persistBuilding = async (effectiveMonth = null) => {
      isSubmitting.value = true;
      try {
        await updateBuilding(
          buildingId,
          building.value,
          { include_visibility_hidden: true },
          effectiveMonth
        );
        store.commit('setFlashMessage', {message: '建物情報更新完了！', type: 'success'});
        router.push(`/buildings/${buildingId}`);
      } catch (error) {
        if (error.response && error.response.data) {
          // エラーメッセージをerrorMessagesにセット
          errorMessages.value = error.response.data || {};
          customerUpdateFromError.value = error.response.data.customer_update_from?.[0] || '';
        }
        store.commit('setFlashMessage', {message: '建物情報更新失敗！', type: 'danger'});
        console.error('更新エラー:', error);
      } finally {
        isSubmitting.value = false;
      }
    };

    const submitWithCustomerChange = async () => {
      if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(customerUpdateFrom.value)) {
        customerUpdateFromError.value = '適用開始年月を入力してください。';
        return;
      }

      await persistBuilding(customerUpdateFrom.value);
    };

    const handleSubmit = async () => {
      if (customerChanged()) {
        openCustomerChangeModal();
        return;
      }

      await persistBuilding();
    };

    onMounted(loadBuildingData);

    return {
      title,
      customers,
      buildingTypes,
      building,
      handleSubmit,
      errorMessages,
      showCustomerChangeModal,
      customerUpdateFrom,
      customerUpdateFromError,
      isSubmitting,
      selectedCustomerName,
      originalCustomerName,
      selectedCustomerDeadlineLabel,
      closeCustomerChangeModal,
      submitWithCustomerChange
    };
  }
};
</script>

<style scoped>
.customer-change-modal {
  align-items: center;
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 1rem;
  position: fixed;
  z-index: 1055;
}

.customer-change-modal__backdrop {
  background: rgba(0, 0, 0, 0.5);
  inset: 0;
  position: absolute;
}

.customer-change-modal__dialog {
  background: #fff;
  border-radius: 0.5rem;
  max-width: 540px;
  padding: 1.5rem;
  position: relative;
  width: 100%;
  z-index: 1;
}

.form-container {
  width: 70%;
  padding: 20px 20px 80px 20px;
  margin: auto;
  border: 1px solid #ddd;
  border-radius: 5px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

input[type="text"],
input[type="email"],
input[type="password"] {
  width: 100%;
  padding: 10px;
  margin-bottom: 4px;
  border: 1px solid #ddd;
  border-radius: 5px;
}

.btn-area{
  text-align: center;
}
</style>
